import { forwardRef, type JSX } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import { Flex } from '@dynatrace/strato-components/layouts';
import {
  ChevronLeftSmallIcon,
  ChevronRightSmallIcon,
} from '@dynatrace/strato-icons';

import { useFormFieldContext } from '../../forms-core/hooks/useFormFieldContext.js';
import { SelectDateLabel } from '../calendar-header-label/SelectDateLabel.js';
import { SelectMonthLabel } from '../calendar-header-label/SelectMonthLabel.js';
import { SelectYearLabel } from '../calendar-header-label/SelectYearLabel.js';
import { calendarHeaderButtonCSS } from '../CalendarItem.sty.js';
import { useCalendarContext } from '../hooks/useCalendarContext.js';
import { NUMBER_OF_YEARS } from '../types/calendarConfiguration.js';
import type { CalendarViewType } from '../types/shared-types.js';

/**
 * @internal
 */
export interface CalendarHeaderProps {
  /** The view currently shown to provide the proper text content. */
  view: CalendarViewType;
  /** Calendar header label id used for the 'aria-labelledby' attribute. */
  labelId: string;
}

/** Returns the right values for label, nextView, and the navigation button payload for each view. */
function getViewSpecificProps(view: CalendarViewType): {
  label: JSX.Element;
  nextView: CalendarViewType;
  payload: { amount: number; unit?: CalendarViewType };
} {
  switch (view) {
    case 'day':
      return {
        label: <SelectDateLabel />,
        nextView: 'month',
        payload: { amount: 1, unit: 'month' },
      };
    case 'month':
      return {
        label: <SelectMonthLabel />,
        nextView: 'year',
        payload: { amount: 1, unit: 'year' },
      };
    case 'year':
      return {
        label: <SelectYearLabel />,
        nextView: 'day',
        payload: { amount: NUMBER_OF_YEARS },
      };
  }
}

/**
 * CalendarHeader component
 * Renders the calendar header, including the buttons to navigate to the previous/next
 * month/year/list of years and the button (label) to switch the calendar view
 */
export const CalendarHeader = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  CalendarHeaderProps
>((props: CalendarHeaderProps, forwardedRef) => {
  const { labelId, view } = props;
  const { dispatch } = useCalendarContext();
  const { disabled, ariaDisabled } = useFormFieldContext();

  const intl = useIntl();

  const { label, nextView, payload } = getViewSpecificProps(view);
  return (
    <Flex justifyContent="space-between" marginBottom={16}>
      <Button
        disabled={disabled}
        aria-disabled={ariaDisabled}
        aria-label={intl.formatMessage({
          defaultMessage: 'Go to previous page.',
          id: 'VoqQWp9yEM0QM8eF',
          description: 'Aria label to add to the `previous` button',
        })}
        onClick={() =>
          dispatch({
            type: 'navigateCurrentView',
            payload: { ...payload, amount: payload.amount * -1 },
          })
        }
      >
        <Button.Prefix>
          <ChevronLeftSmallIcon />
        </Button.Prefix>
      </Button>
      <Button
        className={calendarHeaderButtonCSS}
        disabled={disabled}
        aria-disabled={ariaDisabled}
        aria-live="polite"
        id={labelId}
        onClick={() =>
          dispatch({
            type: 'currentViewChange',
            payload: nextView,
          })
        }
        ref={forwardedRef}
      >
        {label}
      </Button>
      <Button
        disabled={disabled}
        aria-disabled={ariaDisabled}
        aria-label={intl.formatMessage({
          defaultMessage: 'Go to next page.',
          id: 'KAAzM9PVtiJno9ks',
          description: 'Aria label to add to the `next` button',
        })}
        onClick={() =>
          dispatch({
            type: 'navigateCurrentView',
            payload,
          })
        }
      >
        <Button.Prefix>
          <ChevronRightSmallIcon />
        </Button.Prefix>
      </Button>
    </Flex>
  );
});
(
  CalendarHeader as typeof CalendarHeader & { displayName: string }
).displayName = 'CalendarHeader';
